import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrandLockup } from '@/components/BrandMark';
import { Card, Field, PrimaryButton, inputClass } from '@/components/Card';
import { Chip, ChipRow } from '@/components/ChipRow';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

export function GatePage() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function guest() {
    setBusy(true);
    setError(null);
    try {
      const data = await api.guest(name || 'Guest');
      setUser(data.user);
      navigate('/app');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start guest mode.');
    } finally {
      setBusy(false);
    }
  }

  async function household() {
    setBusy(true);
    setError(null);
    try {
      const data = await api.login(passphrase, name || 'Household');
      setUser(data.user);
      navigate('/app');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not unlock household.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative mx-auto flex min-h-screen max-w-[430px] flex-col bg-sand px-5 pb-10 pt-10">
      <div className="absolute inset-x-0 top-0 h-[3px] bg-navy" />
      <p className="text-center text-[11px] font-semibold uppercase tracking-[0.22em] text-brass">
        BARRIER ISLAND DIGITAL
      </p>
      <h1 className="mt-5">
        <BrandLockup variant="hero" />
      </h1>
      <p className="mt-3 text-center text-sm leading-relaxed text-muted">
        Not a lounge directory. A trip-specific decision: what exists, what you can use, what is already covered, and when to book.
      </p>
      <ChipRow className="mt-4 justify-center">
        <Chip tone="navy">EWR</Chip>
        <Chip>PBI</Chip>
        <Chip>RSW</Chip>
        <Chip>BNA</Chip>
        <Chip tone="brass">United-focused</Chip>
      </ChipRow>

      <Card className="mt-8 space-y-4">
        <Field label="What should we call you">
          <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Chip" />
        </Field>
        <PrimaryButton disabled={busy} onClick={guest}>
          Continue as guest
        </PrimaryButton>
        <p className="text-[12px] text-muted">
          Guest mode is labeled on every screen. Wallet and trips persist in this browser session’s cookie, not a synced account.
        </p>
      </Card>

      <Card className="mt-4 space-y-4">
        <h2 className="font-display text-2xl text-navy">Household passphrase</h2>
        <Field label="Passphrase">
          <input
            className={inputClass}
            type="password"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            placeholder="Household passphrase"
          />
        </Field>
        <PrimaryButton disabled={busy || !passphrase} onClick={household}>
          Unlock household
        </PrimaryButton>
        <p className="text-[12px] text-muted">
          Simple shared passphrase — not bank-grade auth. If it is not configured on the Worker, use guest mode.
        </p>
      </Card>

      {error && <p className="mt-4 text-sm text-alert">{error}</p>}
    </div>
  );
}
